import { resolveLoansAppRole } from './app-role';
import type { AvailableTenant, User } from '@/types';

const LOANS_ACTIVE_TENANT_STORAGE_KEY = 'loans_active_tenant_id';
const LOANS_ACTIVE_TENANT_ROLE_KEYS = [
  `${LOANS_ACTIVE_TENANT_STORAGE_KEY}:loans_admin`,
  `${LOANS_ACTIVE_TENANT_STORAGE_KEY}:loans_user`,
] as const;

type TenantContextUser =
  | {
      availableTenants?: AvailableTenant[];
      allowedTenantIds?: string[];
      appRole?: string | null;
      role?: string | null;
      tenantId?: string | null;
    }
  | null
  | undefined;

function resolveActiveTenantStorageKey(user?: TenantContextUser) {
  return `${LOANS_ACTIVE_TENANT_STORAGE_KEY}:${resolveLoansAppRole(user)}`;
}

export function getStoredActiveTenantId(user?: TenantContextUser): string | null {
  const scopedStorageKey = resolveActiveTenantStorageKey(user);
  const scopedTenantId = localStorage.getItem(scopedStorageKey);
  if (scopedTenantId) {
    return scopedTenantId;
  }

  return localStorage.getItem(LOANS_ACTIVE_TENANT_STORAGE_KEY);
}

export function setStoredActiveTenantId(tenantId: string | null, user?: TenantContextUser) {
  const scopedStorageKey = user ? resolveActiveTenantStorageKey(user) : null;

  if (!tenantId) {
    localStorage.removeItem(LOANS_ACTIVE_TENANT_STORAGE_KEY);
    if (scopedStorageKey) {
      localStorage.removeItem(scopedStorageKey);
      return;
    }

    LOANS_ACTIVE_TENANT_ROLE_KEYS.forEach(storageKey => localStorage.removeItem(storageKey));
    return;
  }

  if (scopedStorageKey) {
    localStorage.setItem(scopedStorageKey, tenantId);
  }
  localStorage.removeItem(LOANS_ACTIVE_TENANT_STORAGE_KEY);
}

export function resolveAvailableTenants(user?: TenantContextUser): AvailableTenant[] {
  if (user?.availableTenants?.length) {
    const activeAppRole = resolveLoansAppRole(user);
    const roleScopedTenants = user.availableTenants.filter(
      tenant => tenant.appRole === activeAppRole,
    );

    return roleScopedTenants;
  }

  return (user?.allowedTenantIds || []).map(tenantId => ({
    id: tenantId,
    name: tenantId,
  }));
}

export function resolveActiveTenantId(user?: TenantContextUser): string | null {
  const availableTenants = resolveAvailableTenants(user);
  const allowedTenantIds = new Set(availableTenants.map(tenant => tenant.id));
  const storedTenantId = getStoredActiveTenantId(user);

  if (storedTenantId && allowedTenantIds.has(storedTenantId)) {
    return storedTenantId;
  }

  if (user?.tenantId && allowedTenantIds.has(user.tenantId)) {
    return user.tenantId;
  }

  const firstTenantId = availableTenants[0]?.id || null;
  if (firstTenantId) {
    setStoredActiveTenantId(firstTenantId, user);
  } else {
    setStoredActiveTenantId(null, user);
  }

  return firstTenantId;
}

export function withResolvedTenantContext(user: User): User {
  const availableTenants = resolveAvailableTenants(user);
  const tenantId = resolveActiveTenantId({
    ...user,
    availableTenants,
  });
  if (!tenantId || tenantId === user.tenantId) {
    return {
      ...user,
      availableTenants,
    };
  }

  return {
    ...user,
    availableTenants,
    tenantId,
  };
}
